import { useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import type { User } from '../../userTypes';
import { isAwaitingActivation } from '../../utils/awaitingActivation';
import { canSendPasswordLink } from '../../utils/passwordLinkAccess';

interface UserRowActionsProps {
  user: User;
  /** Whether the caller has `modify` on `users`. No actions are shown without it. */
  canModify: boolean;
  /** Connected user's role — "Reset password" is admin-only (HOMCO-1366 decision). */
  callerRole: string | null;
  /** Connected user's username — used to detect the caller's own row. */
  callerUsername: string | null;
  /** Whether the caller has `delete` on `users`. */
  canDelete: boolean;
  /** Open the user fiche to edit it (HOMCO-623/1364). */
  onEdit: (user: User) => void;
  onResetPassword: (user: User) => void;
  onDeactivate: (user: User) => void;
  onReactivate: (user: User) => void;
  onDelete: (user: User) => void;
}

/**
 * The actions menu is portaled outside the page (and outside AppModal), so it
 * doesn't inherit the readable text color — the theme's `text.primary` is white.
 * Force the body color on items and a muted tone on the leading icons.
 */
const MENU_SX = {
  '& .MuiMenuItem-root': { color: 'text.body' },
  '& .MuiListItemText-primary': { color: 'text.body' },
  '& .MuiListItemIcon-root': { color: 'text.muted' },
} as const;

/**
 * Per-row actions menu for the users list (HOMCO-1366). Visibility rules:
 * - nothing at all without `modify`;
 * - "Reset password" / "Resend activation link": `canSendPasswordLink` (admin +
 *   `modify`, not the caller's own row) and the target is active — hidden otherwise;
 * - "Deactivate" (active) / "Reactivate" (inactive): `modify`. Deactivate is
 *   disabled on the caller's own row with the R7 tooltip.
 */
export function UserRowActions({
  user,
  canModify,
  canDelete,
  callerRole,
  callerUsername,
  onEdit,
  onResetPassword,
  onDeactivate,
  onReactivate,
  onDelete,
}: UserRowActionsProps) {
  const { t } = useTranslation('users');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = anchorEl !== null;

  if (!canModify && !canDelete) return null;

  const isSelf = callerUsername !== null && user.username === callerUsername;
  const showReset =
    canSendPasswordLink({ callerRole, callerUsername, canModify, target: user }) && user.is_active;

  const close = () => setAnchorEl(null);
  // The list row is itself clickable (opens the fiche, HOMCO-623). Stop the trigger and
  // every menu item from bubbling to it, so acting on a row never also navigates away.
  const run = (action: (u: User) => void) => (event: ReactMouseEvent) => {
    event.stopPropagation();
    close();
    action(user);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('actions.menu.aria', { username: user.username })}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={e => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        // The table row navigates to the fiche on click AND on Enter/Space (HOMCO-623):
        // stop the keydown too, or opening the menu from the keyboard would also navigate.
        onKeyDown={e => e.stopPropagation()}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        slotProps={{ list: { sx: MENU_SX } }}
        // React events bubble through the component tree even from the portal, so a
        // click or key press inside the menu would reach the row's handlers. The menu
        // items stop their own clicks (`run`), but a click on the empty backdrop (to
        // dismiss the menu) would otherwise bubble to the row's onClick and navigate
        // to the fiche — stop both click and keydown here.
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        {canModify && (
          <MenuItem onClick={run(onEdit)}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('actions.edit.item')}</ListItemText>
          </MenuItem>
        )}

        {showReset && (
          <MenuItem onClick={run(onResetPassword)}>
            <ListItemIcon>
              <LockResetIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t(isAwaitingActivation(user) ? 'actions.activation.item' : 'actions.reset.item')}
            </ListItemText>
          </MenuItem>
        )}

        {canModify && user.is_active && isSelf && (
          <Tooltip title={t('actions.deactivate.selfTooltip')}>
            <MenuItem
              aria-disabled="true"
              onClick={e => e.preventDefault()}
              sx={{ opacity: 0.38, cursor: 'default' }}
            >
              <ListItemIcon>
                <BlockIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('actions.deactivate.item')}</ListItemText>
            </MenuItem>
          </Tooltip>
        )}
        {canModify && user.is_active && !isSelf && (
          <MenuItem onClick={run(onDeactivate)}>
            <ListItemIcon>
              <BlockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('actions.deactivate.item')}</ListItemText>
          </MenuItem>
        )}
        {canModify && !user.is_active && (
          <MenuItem onClick={run(onReactivate)}>
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('actions.reactivate.item')}</ListItemText>
          </MenuItem>
        )}
        {canModify && canDelete && !isSelf && <Divider key="delete-divider" />}
        {canDelete && !isSelf && user.is_active && (
          <Tooltip title={t('delete.tooltipActive')}>
            <MenuItem
              aria-disabled="true"
              onClick={e => e.preventDefault()}
              sx={{ opacity: 0.38, cursor: 'default' }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('delete.button')}</ListItemText>
            </MenuItem>
          </Tooltip>
        )}
        {canDelete && !isSelf && !user.is_active && (
          <MenuItem onClick={run(onDelete)}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('delete.button')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
